import { Bar, Radar } from 'react-chartjs-2'
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend,
} from 'chart.js'
import type { Submission } from '../../lib/types'
import { count, pct, mean, sd, med, f1 } from '../../lib/stats'
import { C } from '../../lib/colors'

ChartJS.register(CategoryScale, LinearScale, BarElement, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

const BASE = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
const GRID_COLOR = 'rgba(1,87,98,0.07)'

const FIELDS  = ['score_prolactin', 'score_androgens', 'score_insulin', 'score_thyroid', 'score_mood', 'score_cycle'] as const
const LABELS  = ['Prolactin', 'Androgens', 'Insulin', 'Thyroid', 'Mood', 'Cycle']
const MAXES   = [8, 8, 10, 10, 20, 20]
const SIGS    = [3, 3, 3, 4, null, null]
const TIER_LABELS = ['Attuned', 'Listening In', 'Urgent Conv.', 'Priority Review']

interface Props { data: Submission[] }

export function Domains({ data }: Props) {
  const scores = FIELDS.map(f => data.map(d => d[f]))
  const means  = scores.map(mean)
  const sds    = scores.map(sd)
  const meds   = scores.map(med)
  const mins   = scores.map(s => Math.min(...s))
  const maxs   = scores.map(s => Math.max(...s))

  const tierData = [1, 2, 3, 4].map(t => {
    const tg = data.filter(d => d.tier === t)
    return FIELDS.map((f, i) => tg.length ? parseFloat(f1(mean(tg.map(d => d[f])) / MAXES[i] * 100)) : 0)
  })

  return (
    <section className="sec" id="domains">
      <div className="sec-hd">
        <div className="sec-label">04 · Domain Score Analysis</div>
        <h2 className="sec-title">How do individual scoring domains compare?</h2>
        <p className="sec-sub">Six domains: Prolactin (max 8) · Androgens (max 8) · Insulin (max 10) · Thyroid (max 10) · Mood (max 20) · Cycle (max 20).</p>
      </div>
      <div className="g32">
        <div className="card">
          <div className="card-title">Domain mean scores</div>
          <div className="card-sub">Mean score per domain across all respondents (absolute, not normalised)</div>
          <div className="cw h220">
            <Bar
              data={{ labels: LABELS, datasets: [{ data: means.map(v => parseFloat(f1(v))), backgroundColor: C.domain, borderRadius: 4 }] }}
              options={{ ...BASE, indexAxis: 'y' as const, scales: { x: { grid: { color: GRID_COLOR } }, y: { grid: { display: false } } } }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Domain profile by tier</div>
          <div className="card-sub">Normalised (% of max) by tier — each tier's hormonal fingerprint</div>
          <div className="cw h220">
            <Radar
              data={{
                labels: LABELS,
                datasets: C.tier.map((col, i) => ({
                  label: `Tier ${i + 1} · ${TIER_LABELS[i]}`,
                  data: tierData[i],
                  backgroundColor: C.a(col, 0.12),
                  borderColor: col, borderWidth: 1.5, pointRadius: 2,
                })),
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                scales: { r: { beginAtZero: true, max: 100, ticks: { display: false }, grid: { color: GRID_COLOR }, pointLabels: { font: { size: 10 } } } },
                plugins: { legend: { display: true, position: 'bottom' as const, labels: { boxWidth: 10, padding: 8, font: { size: 10 } } } },
              }}
            />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-title">Domain statistics table</div>
        <div className="card-sub">Mean · SD · Median · Range · Signal prevalence</div>
        <div style={{ overflowX: 'auto' }}>
          <table className="dt">
            <thead>
              <tr>
                <th>Domain</th><th>Max</th><th>Mean</th><th>± SD</th><th>Median</th>
                <th>Min</th><th>Max obs.</th><th>Signal threshold</th><th>% Signalled</th>
              </tr>
            </thead>
            <tbody>
              {LABELS.map((lbl, i) => {
                const sigThr = SIGS[i]
                let sigBadge: React.ReactNode
                if (sigThr !== null) {
                  const sp = pct(count(scores[i], s => s >= sigThr), data.length)
                  sigBadge = <span className={`sb ${sp >= 40 ? 'sig' : 'clear'}`}>{sp}%</span>
                } else {
                  const ab = pct(count(scores[i], s => s >= meds[i]), data.length)
                  sigBadge = <span className="sb" style={{ background: 'var(--alt)', color: 'var(--t3)' }}>{ab}%*</span>
                }
                return (
                  <tr key={lbl}>
                    <td><span className="dsw" style={{ background: C.domain[i] }} />{lbl}</td>
                    <td className="m">{MAXES[i]}</td>
                    <td className="m">{f1(means[i])}</td>
                    <td className="m">± {f1(sds[i])}</td>
                    <td className="m">{f1(meds[i])}</td>
                    <td className="m">{mins[i]}</td>
                    <td className="m">{maxs[i]}</td>
                    <td className="m">{sigThr !== null ? `≥ ${sigThr}` : '—'}</td>
                    <td>{sigBadge}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="method">Signal thresholds: Prolactin ≥ 3 · Androgens ≥ 3 · Insulin ≥ 3 · Thyroid ≥ 4 · Mood and Cycle: prevalence above median shown (no binary signal threshold).</p>
      </div>
    </section>
  )
}
